import React, { useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  InputAdornment,
  Container,
  useTheme,
  alpha,
  Link
} from '@mui/material';
import {
  Email,
  Lock,
  Login as LoginIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { checkIfEntraUser, redirectToEntraLogin } from '../../services/oauthService';
import ModernTextField from '../Common/ModernTextField';
import ModernButton from '../Common/ModernButton';
import Logo from '../Common/Logo';

const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [step, setStep] = useState<'email' | 'password'>('email'); // Two-step flow

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    try {
      // Check if user should use Entra ID
      const result = await checkIfEntraUser(email);

      // Production: Always use Entra ID (no local login)
      // Development (localhost): Allow local login fallback
      const isProduction = globalThis.location.hostname !== 'localhost';

      if (result.hasEntraId || isProduction) {
        // Show redirect message
        setInfo(`Preusmeravanje na Microsoft bezbednu prijavu...`);
        setLoading(false);

        // Delay redirect slightly so user sees the message
        setTimeout(async () => {
          await redirectToEntraLogin(email, result.entraUpn);
        }, 1500);
        return;
      }

      // Development only: Local user - show password field
      setStep('password');
    } catch (err: any) {
      setError(err.message || 'Greška pri proveri korisnika.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    try {
      await login(email, password);
      // On successful login or 2FA requirement, AuthContext will handle navigation or modal.
    } catch (err: any) {
      setError(err.message || 'Neuspešna prijava. Proverite vaše podatke.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setError('');
    setInfo('');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4,
      px: 2
    }}>
      <Container maxWidth="md">
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: 0,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          borderRadius: 4,
          overflow: 'hidden',
          minHeight: { md: '600px' }
        }}>
          {/* Left Panel - Branding */}
          <Paper sx={{
            flex: { xs: '0 0 auto', md: '1 1 50%' },
            background: `linear-gradient(135deg,
              ${theme.palette.primary.main} 0%,
              ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              opacity: 0.3
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '500px' }}>
              {/* Logo */}
              <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <Logo
                  variant="icon"
                  colorScheme="light"
                  height={80}
                />
                <Typography variant="h3" component="h1" sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}>
                  OmniPass
                </Typography>
              </Box>

              <Typography variant="h5" sx={{
                fontWeight: 600,
                mb: 3,
                opacity: 0.95
              }}>
                Profesionalno rešenje za digitalne kartice
              </Typography>

              <Typography variant="body1" sx={{
                fontSize: '1.1rem',
                lineHeight: 1.6,
                mb: 4,
                opacity: 0.9
              }}>
                Upravljajte članskim karticama, kuponima i ulaznicama kroz jednu naprednu B2B platformu.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <SecurityIcon sx={{ fontSize: 24, opacity: 0.8 }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Sigurna prijava sa 2FA zaštitom
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Right Panel - Login Form */}
          <Paper sx={{
            flex: { xs: '0 0 auto', md: '1 1 50%' },
            background: alpha(theme.palette.background.paper, 0.98),
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 1
              }}>
                {step === 'email' ? 'Dobrodošli nazad' : 'Unesite lozinku'}
              </Typography>

              <Typography variant="body1" sx={{
                color: 'text.secondary',
                fontSize: '1.1rem'
              }}>
                {step === 'email'
                  ? 'Prijavite se na vaš poslovni nalog'
                  : `Prijavite se kao ${email}`}
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  width: '100%',
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                {error}
              </Alert>
            )}

            {info && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  width: '100%',
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              >
                {info}
              </Alert>
            )}

            {/* Step 1: Email */}
            {step === 'email' && (
              <Box component="form" onSubmit={handleEmailSubmit} sx={{ width: '100%' }}>
                <ModernTextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email adresa"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <ModernButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !email}
                  loading={loading}
                  gradient={true}
                  icon={<LoginIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  Nastavi
                </ModernButton>
              </Box>
            )}

            {/* Step 2: Password (for local users only) */}
            {step === 'password' && (
              <Box component="form" onSubmit={handlePasswordSubmit} sx={{ width: '100%' }}>
                <ModernTextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Lozinka"
                  id="password"
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  isPassword={true}
                  showPasswordToggle={true}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <ModernButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !password}
                  loading={loading}
                  gradient={true}
                  icon={<LoginIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mb: 2
                  }}
                >
                  Prijavite se
                </ModernButton>

                <ModernButton
                  type="button"
                  fullWidth
                  variant="outlined"
                  onClick={handleBackToEmail}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem'
                  }}
                >
                  Nazad na email
                </ModernButton>
              </Box>
            )}

            <Box sx={{
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}>
                Potreban vam je pristup?{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/contact')}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Kontaktirajte naš tim za kreiranje naloga
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginForm;
